import { Box, Skeleton, Typography } from '@mui/material'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useFarmSubdivisionsList } from '../../../hooks/data/useFarmSubdivisionsList'
import { getDeterministicColor } from '../../../utils/mapDrawHandle'
import { Map } from '../../Map/Map'
import ReportMetricsTable from '../ReportMetricsTable'

export const AttestationReportDetail = ({ year, includedMetrics, farm, onLoadComplete }) => {
  const { data: farmSubdivisions, isLoading, isError } = useFarmSubdivisionsList(farm.id, year)
  const [loadedMetrics, setLoadedMetrics] = useState(new Set())

  useEffect(() => {
    // A failed read must not settle the year: the parent gates PDF generation on every year
    // reporting itself loaded, and an incomplete report is worse than a blocked one.
    if (isError) return
    if (loadedMetrics.size === includedMetrics.length || (!isLoading && !farmSubdivisions?.[0])) {
      onLoadComplete(year)
    }
  }, [
    includedMetrics.length,
    loadedMetrics.size,
    farmSubdivisions,
    isLoading,
    isError,
    year,
    onLoadComplete,
  ])

  const handleMetricLoaded = useCallback(metric => {
    setLoadedMetrics(prev => {
      const updated = new Set(prev)
      updated.add(metric)
      return updated
    })
  }, [])

  const perimeter = farm?.toGeoJSON

  const paddocksLayer = useMemo(
    () =>
      farmSubdivisions?.[0]?.features?.map?.(feature => {
        const { properties } = feature
        return {
          ...feature,
          properties: {
            ...properties,
            color: getDeterministicColor(properties.id || properties.name || Math.random()),
          },
        }
      }),
    [farmSubdivisions],
  )

  if (isLoading) return <Skeleton />

  if (isError) return <Typography color="error">No se pudo cargar el loteo {year}.</Typography>

  const [farmSubdivision] = farmSubdivisions

  if (!farmSubdivision) return null

  return (
    <>
      <div className="html2pdf__page-break" />

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px', marginBottom: '16px' }}>
        <Typography sx={{ fontWeight: 'bold' }} typography="h5">
          Loteo {year}
        </Typography>
        <Map isImage flexDirection="column" mapHeight={650} mapWidth={750} perimeter={perimeter}>
          <Map.Layer layer={paddocksLayer} />
          <Map.Legend layers={[paddocksLayer]} />
        </Map>
      </Box>

      <div className="html2pdf__page-break" />

      {includedMetrics.map(({ namespace, label }) => (
        <Box key={namespace} sx={{ marginBottom: '16px' }}>
          <Typography sx={{ fontWeight: 'bold' }} typography="h5">
            Datos de {label} - {year}
          </Typography>
          <ReportMetricsTable
            key={namespace}
            farmId={farm.id}
            farmSubdivision={farmSubdivision}
            namespace={namespace}
            year={year}
            onMetricLoaded={handleMetricLoaded}
          />
        </Box>
      ))}
    </>
  )
}
